import React, { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import Form from '../../components/Form/Form';
import { ErrorModal } from '../../components/ErrorModal';
import { ConfirmationLinkedAccountModal } from '../../components/UHC';

import { loginFields } from '../../services/Constants/FormConstants';
import { ACTIVATE_UHC_SUBSCRIPTION, SIGNIN } from '../../services/ApolloClient';

import { useAuth, useApp } from '../../contexts';
import { API } from '../../services/API/BaseAPI';
import { navigateWithReload } from '../../utils';
import { EDataKey, TData } from '../../types/forms';

function UhcLogin() {
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [showUhcActivatedModal, setShowUhcActivatedModal] = useState(false);
  const [showUhcActivationErrorModal, setShowUhcActivationErrorModal] = useState(false);

  const [signin] = useMutation(SIGNIN);
  const [activateUHCSubscription] = useMutation(ACTIVATE_UHC_SUBSCRIPTION);

  const { login } = useAuth();
  const { setLoading } = useApp();

  const uhcEmail = localStorage.getItem('uhc_pending_email') ?? '';
  const samlId = localStorage.getItem('uhc_pending_link_id');

  // only allow this page if it came from the UHC flow
  useEffect(() => {
    console.log('UHC Login page loaded with email:', uhcEmail, 'and samlId:', samlId);
    const hasRequiredData = Boolean(uhcEmail) && Boolean(samlId);
    console.log('Does the UHC Login page have the required data?', hasRequiredData);
    if (!hasRequiredData) {
      navigateWithReload('/');
    }
    // only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = useMemo(() => {
    return loginFields.map(field => {
      if (field.key === EDataKey.email) {
        return { ...field, isDisabled: true };
      }
      return field;
    });
  }, []);

  const handleSignin = async (formData: Partial<TData>) => {
    try {
      setLoading(true);
      setFormError({});

      const res = await signin({
        variables: {
          name: (formData?.email ?? uhcEmail)?.trim(),
          password: formData.password,
        },
      });

      login(res.data?.login?.token);
      await API.post('auth-cookie');

      try {
        const shouldLinkAccount = localStorage.getItem('uhc_is_linked_account') === 'false';

        await activateUHCSubscription({
          variables: {
            samlId,
            linkAccount: shouldLinkAccount,
          },
        });

        setShowUhcActivatedModal(true);

        // cleanup
        localStorage.removeItem('uhc_pending_link_id');
        localStorage.removeItem('uhc_pending_email');
        localStorage.removeItem('uhc_pending_link_has_password');
        localStorage.removeItem('uhc_is_linked_account');

        setLoading(false);
      } catch (error) {
        setShowUhcActivationErrorModal(true);
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      setFormError({
        message:
          'Your username or password is invalid. Please note, both email and password fields are case-sensitive.',
        fields: [EDataKey.email, EDataKey.password],
      });
    }
  };

  if (showUhcActivatedModal) {
    return (
      <ConfirmationLinkedAccountModal
        isOpen={showUhcActivatedModal}
        onOkClick={() => navigateWithReload('/app/your-path/')}
      />
    );
  }

  return (
    <div className="register-login-container">
      <Helmet>
        <title>UHC Login | Every Mother</title>
      </Helmet>

      <div className="register-login-content">
        <Form
          fields={fields}
          handleForm={handleSignin}
          formError={formError}
          buttonLabel="Continue"
          formData={{ email: uhcEmail }}
        >
          <Link className="navigate-link forgot-password-link" to="/forgot-password">
            forgot password?
          </Link>
        </Form>
      </div>

      <ErrorModal
        isOpen={showUhcActivationErrorModal}
        title="UHC Activation Failed"
        message="There was a problem activating your UHC subscription. Please try again or contact support."
        buttonText="Go Home"
        onSubmit={() => {
          setShowUhcActivationErrorModal(false);
          navigateWithReload('/');
        }}
      />
    </div>
  );
}

export default UhcLogin;
